// App Migration Inline Banner Component (PLP Mini Banner)
// Displays a compact horizontal banner within the PLP product grid
// Shows after every 2 scrolls (4 products in 2-column grid)

import React, { memo, useState, useCallback, useEffect } from 'react';

// Helpers
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';

// Hooks
import {
  useAppInstallDetection,
  useAppsFlyerSmartScript,
} from 'shared/components/AppMigration/hooks';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// Components
import AppMigrationBottomSheet from 'shared/components/AppMigration/AppMigrationBottomSheet';

// Helpers
import { isIOSDevice, navigateToURL } from 'shared/components/AppMigration/helpers';

// Constants
import {
  STORE_URLS,
  CTA_TEXT,
  APP_MIGRATION_EXP,
  APP_MIGRATION_EXP_VARIANT_A,
  APP_MIGRATION_EXP_VARIANT_B,
  UTM_CAMPAIGN,
  UTM_CONTENT,
  INLINE_BANNER_IMAGE_URL,
} from 'shared/components/AppMigration/constants';

// Styles
import {
  BannerContainer,
  CardContainer,
  CardWrapper,
  BannerImage,
  TextSection,
  Title,
  Description,
  CTAButton,
} from './styles';

// Types
export interface IAppMigrationInlineBannerProps {
  // eslint-disable-next-line react/no-unused-prop-types
  position?: number; // Position in the PLP grid for analytics
  pageType?: string;
  onCTAClick?: () => void;
}

/**
 * Preload images for faster rendering
 */
const preloadInlineBannerImages = (urls: string[]): void => {
  if (typeof window === 'undefined') return;

  urls.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
};

/**
 * AppMigrationInlineBanner - A compact horizontal banner displayed within PLP product grid
 *
 * Features:
 * - Dark navy background with phone mockups
 * - Clickable to trigger app download/open
 * - Integrates with AppsFlyer Smart Script
 *
 * @param pageType - Page type for analytics tracking
 * @param onCTAClick - Callback when CTA is clicked
 */
function AppMigrationInlineBanner({
  pageType = 'plp',
  onCTAClick,
}: IAppMigrationInlineBannerProps) {
  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  // Remote config for content
  const appMigrationEnabled = useRemoteConfig('appMigrationEnabled');
  const appMigrationContent = useRemoteConfig('appMigrationContent');
  const inlineBannerConfig = appMigrationContent?.inlineBanner;

  // Content from remote config with fallbacks
  const bannerTitle = inlineBannerConfig?.title || 'Browse faster on the app';
  const bannerDescription =
    inlineBannerConfig?.description || 'Install now to get exciting app-only offers';
  const ctaText = inlineBannerConfig?.ctaText || 'Download Now';

  // Image URL from remote config with fallback
  const backgroundImageUrl = inlineBannerConfig?.backgroundImageUrl || INLINE_BANNER_IMAGE_URL;

  // AB Experiment for feature flag
  const { isEligible = false, variant = '' } = getEligibleVariantName(APP_MIGRATION_EXP);
  const isFeatureEnabled =
    !!appMigrationEnabled &&
    isEligible &&
    (variant === APP_MIGRATION_EXP_VARIANT_A || variant === APP_MIGRATION_EXP_VARIANT_B);

  // App installation detection
  const { isLoading: isDetecting, isMobile, platform, isAppInstalled } = useAppInstallDetection();

  // AppsFlyer Smart Script - only load when feature is enabled and on mobile
  const autoLoad = isFeatureEnabled && isMobile;
  const { generateURL } = useAppsFlyerSmartScript({
    autoLoad: autoLoad,
    additionalParams: {
      utmCampaign: UTM_CAMPAIGN,
      utmMedium: pageType,
      utmContent: `${pageType} ${UTM_CONTENT.INLINE_BANNER}`,
      trackingCode: variant,
    },
  });

  // Initialize banner visibility
  useEffect(() => {
    // Wait for detection to complete
    if (isDetecting) return;

    // Check if feature is enabled via AB experiment
    if (!isFeatureEnabled) {
      setIsLoading(false);
      return;
    }

    // Only show on mobile web
    if (!isMobile) {
      setIsLoading(false);
      return;
    }

    // Preload image
    preloadInlineBannerImages([backgroundImageUrl]);

    // Show the banner
    setIsVisible(true);
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetecting, isMobile, isFeatureEnabled]);

  /**
   * Handle banner click - opens the bottom sheet
   */
  const handleBannerClick = useCallback(() => {
    setIsBottomSheetOpen(true);
  }, []);

  /**
   * Handle bottom sheet close
   */
  const handleBottomSheetClose = useCallback(() => {
    setIsBottomSheetOpen(false);
  }, []);

  /**
   * Handle download button click in bottom sheet - triggers app download/open
   */
  const handleDownload = useCallback(async () => {
    // Generate AppsFlyer URL and redirect
    const oneLinkURL = await generateURL();

    if (oneLinkURL) {
      // Navigate via anchor click so Android App Links can intercept
      navigateToURL(oneLinkURL);
    } else {
      // Fallback to store URLs
      if (isIOSDevice()) {
        navigateToURL(STORE_URLS.APP_STORE);
      } else {
        navigateToURL(STORE_URLS.PLAY_STORE);
      }
    }

    if (onCTAClick) {
      onCTAClick();
    }
  }, [generateURL, onCTAClick]);

  // Don't render during SSR or loading
  if (typeof window === 'undefined' || isLoading) {
    return null;
  }

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <BannerContainer data-testid="app-migration-inline-banner" pageType={pageType}>
      <CardWrapper>
        <CardContainer onClick={handleBannerClick}>
          {/* Single background image (includes phone mockups) */}
          <BannerImage src={backgroundImageUrl} alt="" loading="lazy" />

          {/* Text Content - Left Side */}
          <TextSection>
            <Title>{bannerTitle}</Title>
            <Description>{bannerDescription}</Description>
            <CTAButton>{ctaText}</CTAButton>
          </TextSection>
        </CardContainer>
      </CardWrapper>

      {/* Bottom Sheet for App Download */}
      <AppMigrationBottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleBottomSheetClose}
        onDownload={handleDownload}
        ctaText={
          platform === 'android' && isAppInstalled ? CTA_TEXT.USE_APP : CTA_TEXT.DOWNLOAD_NOW
        }
      />
    </BannerContainer>
  );
}

export default memo(AppMigrationInlineBanner);
